





























































import { computed, defineComponent, toRef } from "@vue/composition-api";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import { UserPublicData as OrigUserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useNSFWUserSettings from "@/hooks/useNSFWUserSettings";
import useRole from "@/hooks/useRole";
import { MediaFilesController } from "@/lib/MediaFilesController";
import config from "@/config";
import BaseIcon from "@/components/Base/BaseIcon";
import CreatePostButtonController from "@/components/CreatePostButtonController";
import SubscriberIcon from "@/views/PageMessageThreads/components/SubscriberIcon.vue";
import AvatarBorderAnimation from "./components/AvatarBorderAnimation";

enum SIZE {
  BIG = "BIG",
  MEDIUM = "MEDIUM",
  SMALL = "SMALL",
}

export default defineComponent({
  name: "BaseAvatar",
  components: { CreatePostButtonController, AvatarBorderAnimation, SubscriberIcon, BaseIcon },
  props: {
    size: { type: [Number, String], default: 112 },
    user: { type: Object as () => UserPublicData | OrigUserPublicData | User | null },
    hideOnlineStatus: { type: Boolean, default: false },
    hideBroadcasting: { type: Boolean, default: false },
    animate: { type: Boolean, default: false },
    showBlockStatus: { type: Boolean, default: false },
    showStoryCreateBtn: { type: Boolean, default: false },
    showStoryInfo: { type: Boolean, default: false },
    whiteBorder: { type: Boolean, default: false },
  },
  setup(props) {
    const { authUser } = useAuthUser();
    const { showNotSafeForWork } = useNSFWUserSettings();
    const { isCreator } = useRole(toRef(props, "user"));
    const src = computed(
      () =>
        (props.user && props.user.avatar && MediaFilesController.getImageThumbnail(props.user.avatar.urlPart, 224)) ||
        ""
    );
    const isAuthor = computed(() => authUser.value?.id === props.user?.id);
    const invisible = computed(
      () => !!props.user && "onlineStatus" in props.user && !!props.user.onlineStatus?.invisible && isAuthor.value
    );
    const blurred = computed(
      () =>
        !isAuthor.value &&
        isCreator.value &&
        !!props.user &&
        "nsfwSettings" in props.user &&
        !props.user.nsfwSettings?.safeForWork &&
        !showNotSafeForWork.value
    );
    const online = computed(
      () =>
        (config.featureUserOnlineStatus &&
          props.user &&
          "onlineStatus" in props.user &&
          props.user.onlineStatus &&
          props.user.onlineStatus.isOnline &&
          !props.user.onlineStatus.invisible &&
          !props.hideOnlineStatus) ||
        false
    );
    const live = computed(
      () =>
        props.user &&
        "publicLivechatUser" in props.user &&
        !!props.user.publicLivechatUser &&
        props.user.onlineStatus &&
        !props.hideBroadcasting &&
        !props.user.onlineStatus.invisible
    );
    const hasStories = computed(
      () =>
        config.featureStories &&
        props.showStoryInfo &&
        props.user &&
        "hasStories" in props.user &&
        props.user.hasStories
    );
    const hasNewStories = computed(
      () =>
        config.featureStories &&
        props.showStoryInfo &&
        props.user &&
        "firstNotViewedStoryId" in props.user &&
        !!props.user.firstNotViewedStoryId
    );
    const sizeDimension = computed(() => (typeof props.size === "string" ? props.size : `${props.size}px`));
    const sizeNumber = computed(() => parseInt(sizeDimension.value));
    const liveBorderWidth = computed(() => (sizeNumber.value > 100 ? 4 : sizeNumber.value > 50 ? 3 : 2));
    const elementSize = computed<SIZE>(() =>
      sizeNumber.value >= 100 ? SIZE.BIG : sizeNumber.value >= 50 ? SIZE.MEDIUM : SIZE.SMALL
    );
    const borderWidth = computed(() =>
      elementSize.value === SIZE.BIG ? 4 : elementSize.value === SIZE.MEDIUM ? 3 : 2
    );
    const subscriberCost = computed(() =>
      props.user && "subscriptionToMe" in props.user ? (props.user.subscriptionToMe?.amount ?? 0) : 0
    );

    return {
      src,
      online,
      live,
      sizeDimension,
      sizeNumber,
      liveBorderWidth,
      subscriberCost,
      invisible,
      elementSize,
      borderWidth,
      blurred,
      hasStories,
      hasNewStories,
      SIZE,
      ContentFeedTypeEnum,
    };
  },
});
